import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ordersApi } from '../services/api';
import { Order, OrderStatus } from '../types';

const statusColors: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-800',
  pending_payment: 'bg-yellow-100 text-yellow-800',
  submitted: 'bg-yellow-100 text-yellow-800',
  pending_release: 'bg-orange-100 text-orange-800',
  approved: 'bg-blue-100 text-blue-800',
  in_production: 'bg-purple-100 text-purple-800',
  ready_to_ship: 'bg-indigo-100 text-indigo-800',
  shipped: 'bg-cyan-100 text-cyan-800',
  delivered: 'bg-teal-100 text-teal-800',
  install_scheduled: 'bg-blue-200 text-blue-900',
  installed: 'bg-green-200 text-green-900',
  warranty_registered: 'bg-emerald-100 text-emerald-800',
  completed: 'bg-green-100 text-green-800',
  closed: 'bg-green-100 text-green-800',
  needs_correction: 'bg-orange-100 text-orange-800',
  cancelled: 'bg-red-100 text-red-800',
  lost: 'bg-gray-200 text-gray-600',
  on_hold: 'bg-orange-100 text-orange-800',
};

const statusLabels: Record<string, string> = {
  draft: 'Draft',
  pending_payment: 'Pending Payment',
  submitted: 'Submitted',
  pending_release: 'Pending Release',
  approved: 'Approved',
  in_production: 'In Production',
  ready_to_ship: 'Ready to Ship',
  shipped: 'Shipped',
  delivered: 'Delivered',
  install_scheduled: 'Install Scheduled',
  installed: 'Installed',
  warranty_registered: 'Warranty Registered',
  completed: 'Completed',
  closed: 'Completed',
  needs_correction: 'Needs Correction',
  cancelled: 'Cancelled',
  lost: 'Lost',
  on_hold: 'On Hold',
};

export const OrdersPage: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filter state
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [orderTypeFilter, setOrderTypeFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Pagination state
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalOrders, setTotalOrders] = useState(0);
  const [pageSize, setPageSize] = useState(20);

  // Mark Lost modal state
  const [showLostModal, setShowLostModal] = useState(false);
  const [lostOrderId, setLostOrderId] = useState<string | null>(null);
  const [lostReason, setLostReason] = useState('');
  const [lostCustomReason, setLostCustomReason] = useState('');
  const [markingLost, setMarkingLost] = useState(false);

  // Schedule Install modal state
  const [showInstallModal, setShowInstallModal] = useState(false);
  const [installOrderId, setInstallOrderId] = useState<string | null>(null);
  const [installDate, setInstallDate] = useState('');
  const [installTime, setInstallTime] = useState('');
  const [installNotes, setInstallNotes] = useState('');
  const [schedulingInstall, setSchedulingInstall] = useState(false);

  const LOST_REASONS = [
    'Budget constraints',
    'Chose competitor',
    'Project cancelled',
    'Timeline changed',
    'Customer unresponsive',
    'Other',
  ];

  // Debounced search
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [statusFilter, orderTypeFilter, debouncedSearch, dateFrom, dateTo, pageSize]);

  // Fetch orders
  useEffect(() => {
    fetchOrders();
  }, [statusFilter, orderTypeFilter, debouncedSearch, dateFrom, dateTo, page, pageSize]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      setError(null);

      const params: any = {
        page,
        limit: pageSize,
      };

      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }

      if (orderTypeFilter !== 'all') {
        params.orderType = orderTypeFilter;
      }

      if (debouncedSearch.trim()) {
        params.search = debouncedSearch.trim();
      }

      if (dateFrom) {
        params.dateFrom = dateFrom;
      }

      if (dateTo) {
        params.dateTo = dateTo;
      }

      const response = await ordersApi.list(params);
      setOrders(response.data || response.items || []);
      setTotalPages(response.totalPages || 1);
      setTotalOrders(response.total || 0);
    } catch (err: any) {
      setError(err.message || 'Failed to load orders');
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkLost = async () => {
    if (!lostOrderId || !lostReason) return;
    const reason = lostReason === 'Other' ? lostCustomReason : lostReason;
    if (!reason.trim()) return;

    setMarkingLost(true);
    try {
      await ordersApi.markLost(lostOrderId, reason.trim());
      setShowLostModal(false);
      setLostOrderId(null);
      setLostReason('');
      setLostCustomReason('');
      fetchOrders();
    } catch (err) {
      console.error('Failed to mark order as lost:', err);
    } finally {
      setMarkingLost(false);
    }
  };

  const handleScheduleInstall = async () => {
    if (!installOrderId || !installDate) return;
    setSchedulingInstall(true);
    try {
      await ordersApi.scheduleInstall(installOrderId, {
        installDate,
        installTime: installTime || undefined,
        notes: installNotes || undefined,
      });
      setShowInstallModal(false);
      setInstallOrderId(null);
      setInstallDate('');
      setInstallTime('');
      setInstallNotes('');
      fetchOrders();
    } catch (err) {
      console.error('Failed to schedule install:', err);
    } finally {
      setSchedulingInstall(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount || 0);
  };

  const clearFilters = () => {
    setStatusFilter('all');
    setOrderTypeFilter('all');
    setSearchQuery('');
    setDateFrom('');
    setDateTo('');
    setPage(1);
  };

  const hasActiveFilters = statusFilter !== 'all' || orderTypeFilter !== 'all' || searchQuery || dateFrom || dateTo;

  // Generate page numbers for pagination
  const pageNumbers = useMemo(() => {
    const pages: (number | string)[] = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible + 2) {
      // Show all pages if total is small
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Show first page
      pages.push(1);

      if (page > 3) {
        pages.push('...');
      }

      // Show pages around current
      const start = Math.max(2, page - 1);
      const end = Math.min(totalPages - 1, page + 1);

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (page < totalPages - 2) {
        pages.push('...');
      }

      // Show last page
      if (totalPages > 1) {
        pages.push(totalPages);
      }
    }

    return pages;
  }, [page, totalPages]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">My Orders</h1>
          <p className="text-gray-400">View and manage your screen orders</p>
        </div>
        <Link
          to="/orders/new"
          className="bg-yellow-500 hover:bg-yellow-600 text-black font-semibold px-6 py-2 rounded-lg transition-colors"
        >
          + New Order
        </Link>
      </div>

      {/* Search & Filters */}
      <div className="card">
        <div className="space-y-4">
          {/* Search Bar */}
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              type="text"
              placeholder="Search by order number, sidemark, PO number, or company..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="input-field pl-10"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-white"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>

          {/* Filter Row */}
          <div className="flex flex-wrap items-center gap-4">
            {/* Status Filter */}
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-400">Status:</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="select-field py-2 px-3 text-sm"
              >
                <option value="all">All Statuses</option>
                {Object.entries(statusLabels).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>

            {/* Order Type Filter */}
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-400">Type:</label>
              <select
                value={orderTypeFilter}
                onChange={(e) => setOrderTypeFilter(e.target.value)}
                className="select-field py-2 px-3 text-sm"
              >
                <option value="all">All Types</option>
                <option value="screen">Screen Orders</option>
                <option value="parts">Parts & Accessories</option>
              </select>
            </div>

            {/* Date Range */}
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-400">From:</label>
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="input-field py-2 px-3 text-sm"
              />
            </div>

            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-400">To:</label>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="input-field py-2 px-3 text-sm"
              />
            </div>

            {/* Clear Filters */}
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="text-sm text-keder-yellow hover:text-yellow-400 font-medium flex items-center gap-1"
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                Clear filters
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Results Summary */}
      {!loading && (
        <div className="text-sm text-gray-400">
          Showing {orders.length > 0 ? ((page - 1) * pageSize + 1) : 0} - {Math.min(page * pageSize, totalOrders)} of {totalOrders} orders
          {hasActiveFilters && ' (filtered)'}
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-900/50 border border-red-500 text-red-300 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Loading State */}
      {loading ? (
        <div className="card p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-keder-yellow mx-auto"></div>
          <p className="mt-2 text-gray-400">Loading orders...</p>
        </div>
      ) : orders.length === 0 ? (
        /* Empty State */
        <div className="card p-8 text-center">
          <div className="text-gray-400 text-5xl mb-4">📋</div>
          <h3 className="text-lg font-medium text-white">No orders found</h3>
          <p className="text-gray-400 mt-1">
            {hasActiveFilters
              ? 'No orders match your search criteria. Try adjusting your filters.'
              : "You haven't placed any orders yet."}
          </p>
          {hasActiveFilters ? (
            <button
              onClick={clearFilters}
              className="inline-block mt-4 bg-keder-gray hover:bg-gray-600 text-white font-medium px-6 py-2 rounded-lg transition-colors"
            >
              Clear Filters
            </button>
          ) : (
            <Link
              to="/orders/new"
              className="inline-block mt-4 btn-primary"
            >
              Create Your First Order
            </Link>
          )}
        </div>
      ) : (
        /* Orders Table */
        <div className="card overflow-hidden p-0">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-keder-gray">
              <thead className="bg-keder-gray/50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Order
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Preview
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    PO / Job Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Items
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Total
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Your Sale
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-keder-gray/50">
                {orders.map((order) => (
                  <tr key={order.id} className="hover:bg-keder-gray/20">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Link
                        to={`/orders/${order.id}`}
                        className="text-keder-yellow hover:text-yellow-400 font-medium"
                      >
                        {order.orderNumber}
                      </Link>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      {(order as any).visualizerImageUrl ? (
                        <img
                          src={(order as any).visualizerImageUrl}
                          alt="Visualizer preview"
                          className="w-12 h-12 object-cover rounded border border-keder-gray"
                        />
                      ) : (
                        <span className="text-gray-500 text-sm">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                      {order.sidemark || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full ${
                          (order as any).orderType === 'parts'
                            ? 'bg-purple-600 text-white'
                            : 'bg-blue-600 text-white'
                        }`}
                      >
                        {(order as any).orderType === 'parts' ? 'Parts' : 'Screen'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span
                          className={`px-2 py-1 text-xs font-medium rounded-full ${
                            statusColors[order.status] || 'bg-gray-600 text-white'
                          }`}
                        >
                          {/* Will-call orders use the same 'shipped' status code as
                              freight orders for downstream logic (production /
                              reports), but for the dealer "Shipped" is wrong wording
                              when they're picking up. Relabel to "Ready for Pickup". */}
                          {(() => {
                            const sm = String((order as any).shippingMethod || (order as any).shipping_method || '').toLowerCase();
                            const isWillCall = sm === 'will_call' || sm === 'willcall';
                            if (isWillCall && order.status === 'shipped') return 'Ready for Pickup';
                            return statusLabels[order.status] || order.status;
                          })()}
                        </span>
                        {((order as any).invoiceCorrectionRequestedAt || (order as any).invoice_correction_requested_at) && (
                          <span
                            className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-red-500/20 text-red-400 flex items-center gap-0.5"
                            title={(order as any).invoiceCorrectionMessage || (order as any).invoice_correction_message || 'Correction requested'}
                          >
                            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                            Correction Pending
                          </span>
                        )}
                        {!((order as any).invoiceCorrectionRequestedAt || (order as any).invoice_correction_requested_at) &&
                         ((order as any).invoiceCorrectionResolvedAt || (order as any).invoice_correction_resolved_at) && (
                          <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-green-500/20 text-green-400 flex items-center gap-0.5">
                            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            Correction Made
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-white">
                      {(() => {
                        // Try multiple sources for item count
                        const o = order as any;
                        let count = o.totalItems || o.itemCount || o.item_count || o.screenCount || o.screen_count || 0;

                        // Try to get from order_data if available
                        if (!count && o.order_data) {
                          try {
                            const data = typeof o.order_data === 'string' ? JSON.parse(o.order_data) : o.order_data;
                            count = data?.invoice?.screens?.length || data?.order?.units?.length || data?.order?.items?.length || 0;
                          } catch (e) {}
                        }

                        // Try items array
                        if (!count && o.items) {
                          count = Array.isArray(o.items) ? o.items.length : 0;
                        }

                        return `${count} ${o.orderType === 'parts' || o.order_type === 'parts' ? 'items' : 'screens'}`;
                      })()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-white">
                      {formatCurrency(order.totalAmount)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {(() => {
                        const o = order as any;
                        const sellPrice = o.customerSellPrice || o.customer_sell_price;

                        if (sellPrice && sellPrice > 0) {
                          return (
                            <span className="text-green-400 font-medium">
                              {formatCurrency(sellPrice)}
                            </span>
                          );
                        }

                        return (
                          <Link
                            to={`/price-builder`}
                            onClick={(e) => {
                              e.preventDefault();
                              // Navigate to Price Builder with this order's data
                              sessionStorage.setItem('orderToPriceBuilder', JSON.stringify({
                                orderId: order.id,
                                orderNumber: order.orderNumber || (order as any).order_number,
                              }));
                              window.location.href = '/price-builder';
                            }}
                            className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-600/20 text-yellow-400 hover:bg-yellow-600/30"
                          >
                            Set Price
                          </Link>
                        );
                      })()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                      {formatDate(order.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <Link
                        to={`/orders/${order.id}`}
                        className="text-keder-yellow hover:text-yellow-400 mr-3"
                      >
                        View
                      </Link>
                      {['draft', 'pending_payment'].includes(order.status) && (
                        <Link
                          to={`/orders/${order.id}/edit`}
                          className="text-blue-400 hover:text-blue-300 mr-3"
                        >
                          Edit
                        </Link>
                      )}
                      {['draft', 'pending_payment'].includes(order.status) && (
                        <button
                          onClick={async () => {
                            if (window.confirm(`Are you sure you want to delete this ${order.status === 'draft' ? 'draft' : 'pending payment'} order?`)) {
                              try {
                                await ordersApi.delete(order.id);
                                fetchOrders();
                              } catch (err) {
                                console.error('Failed to delete order:', err);
                              }
                            }
                          }}
                          className="text-red-400 hover:text-red-300 mr-3"
                        >
                          Delete
                        </button>
                      )}
                      {['draft', 'pending_payment'].includes(order.status) && (
                        <button
                          onClick={() => {
                            setLostOrderId(order.id);
                            setLostReason('');
                            setLostCustomReason('');
                            setShowLostModal(true);
                          }}
                          className="text-gray-400 hover:text-gray-300"
                        >
                          Lost
                        </button>
                      )}
                      {order.orderType !== 'parts' && order.order_type !== 'parts' && !['cancelled', 'closed', 'lost', 'installed', 'warranty_registered'].includes(order.status) && (
                        <button
                          onClick={() => {
                            setInstallOrderId(order.id);
                            setInstallDate(order.installScheduledDate || '');
                            setInstallTime(order.installScheduledTime || '');
                            setInstallNotes(order.installNotes || '');
                            setShowInstallModal(true);
                          }}
                          className="text-blue-400 hover:text-blue-300 ml-3"
                        >
                          {order.installScheduledDate ? 'Reschedule' : 'Schedule Install'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="bg-keder-gray/30 px-6 py-4 flex flex-col sm:flex-row items-center justify-between border-t border-keder-gray gap-4">
            {/* Page Size Selector */}
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-400">Show:</label>
              <select
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                className="select-field px-2 py-1 text-sm"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span className="text-sm text-gray-400">per page</span>
            </div>

            {/* Page Navigation */}
            {totalPages > 1 && (
              <div className="flex items-center gap-1">
                {/* First & Previous */}
                <button
                  onClick={() => setPage(1)}
                  disabled={page === 1}
                  className="px-2 py-1 border border-keder-gray rounded text-sm text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-keder-gray"
                  title="First page"
                >
                  ««
                </button>
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-3 py-1 border border-keder-gray rounded text-sm text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-keder-gray"
                >
                  Previous
                </button>

                {/* Page Numbers */}
                <div className="hidden sm:flex items-center gap-1">
                  {pageNumbers.map((pageNum, idx) =>
                    pageNum === '...' ? (
                      <span key={`ellipsis-${idx}`} className="px-2 text-gray-500">
                        ...
                      </span>
                    ) : (
                      <button
                        key={pageNum}
                        onClick={() => setPage(pageNum as number)}
                        className={`px-3 py-1 border rounded text-sm ${
                          page === pageNum
                            ? 'bg-keder-yellow text-black border-keder-yellow font-medium'
                            : 'border-keder-gray text-gray-300 hover:bg-keder-gray'
                        }`}
                      >
                        {pageNum}
                      </button>
                    )
                  )}
                </div>

                {/* Mobile page indicator */}
                <span className="sm:hidden text-sm text-gray-400 px-2">
                  Page {page} of {totalPages}
                </span>

                {/* Next & Last */}
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="px-3 py-1 border border-keder-gray rounded text-sm text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-keder-gray"
                >
                  Next
                </button>
                <button
                  onClick={() => setPage(totalPages)}
                  disabled={page === totalPages}
                  className="px-2 py-1 border border-keder-gray rounded text-sm text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-keder-gray"
                  title="Last page"
                >
                  »»
                </button>
              </div>
            )}
          </div>
        </div>
      )}
      {/* Schedule Install Modal */}
      {showInstallModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl p-6 w-full max-w-md border border-gray-700">
            <h3 className="text-lg font-semibold text-white mb-4">
              {installDate ? 'Reschedule Install' : 'Schedule Install'}
            </h3>
            <p className="text-gray-400 text-sm mb-4">
              Pick an installation date. It will appear on your Install Calendar for your team.
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Install Date *</label>
                <input
                  type="date"
                  value={installDate}
                  onChange={(e) => setInstallDate(e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Time (optional)</label>
                <input
                  type="time"
                  value={installTime}
                  onChange={(e) => setInstallTime(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Notes (optional)</label>
                <textarea
                  value={installNotes}
                  onChange={(e) => setInstallNotes(e.target.value)}
                  placeholder="Access instructions, customer contact, etc."
                  rows={3}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowInstallModal(false);
                  setInstallOrderId(null);
                }}
                className="flex-1 px-4 py-2 border border-gray-600 text-gray-300 rounded-lg hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={handleScheduleInstall}
                disabled={schedulingInstall || !installDate}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {schedulingInstall ? 'Saving...' : 'Schedule'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mark Lost Modal */}
      {showLostModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl p-6 w-full max-w-md border border-gray-700">
            <h3 className="text-lg font-semibold text-white mb-4">Mark Quote as Lost</h3>
            <p className="text-gray-400 text-sm mb-4">
              Select the reason this quote was lost. This helps track and improve conversion rates.
            </p>

            <div className="space-y-2 mb-4">
              {LOST_REASONS.map(reason => (
                <label
                  key={reason}
                  className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    lostReason === reason
                      ? 'border-yellow-500 bg-yellow-500/10'
                      : 'border-gray-600 hover:border-gray-500'
                  }`}
                >
                  <input
                    type="radio"
                    name="lostReason"
                    value={reason}
                    checked={lostReason === reason}
                    onChange={() => setLostReason(reason)}
                    className="text-yellow-500"
                  />
                  <span className="text-gray-200">{reason}</span>
                </label>
              ))}
            </div>

            {lostReason === 'Other' && (
              <div className="mb-4">
                <input
                  type="text"
                  value={lostCustomReason}
                  onChange={(e) => setLostCustomReason(e.target.value)}
                  placeholder="Enter reason..."
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                />
              </div>
            )}

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowLostModal(false);
                  setLostOrderId(null);
                }}
                className="flex-1 px-4 py-2 border border-gray-600 text-gray-300 rounded-lg hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={handleMarkLost}
                disabled={markingLost || !lostReason || (lostReason === 'Other' && !lostCustomReason.trim())}
                className="flex-1 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-500 disabled:opacity-50"
              >
                {markingLost ? 'Saving...' : 'Mark as Lost'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrdersPage;
